/*
 * PDP Sticky Sidebar — Modern-Style page-spanning sticky.
 *
 * Problem: Shopify PDP sections (main-product, related-products,
 * featured-collection, ...) are sequential siblings under <main>. Putting
 * `position: sticky` on .product__info-wrapper inside main-product limits
 * stickiness to that section's bounding box — sidebar releases as soon as
 * user scrolls past main-product. Previous attempt used `position: fixed`
 * + IntersectionObserver, which fired the hide-on-footer too early because
 * Zippo's footer is 799px tall (1px overlap at viewport bottom triggered
 * threshold:0 while user was still browsing featured-collection).
 *
 * Fix: At >=990px, MOVE .product__info-wrapper out of main-product to be
 * a direct child of <main id="MainContent">. Combined with a CSS Grid on
 * <main> (`grid-template-columns: 1fr 480px` + `grid-row: 1 / span 999`
 * on the wrapper), the sidebar becomes a full-height column spanning all
 * sibling sections. `position: sticky` inside that column then naturally
 * releases at the bottom of the column (= just above <footer>) — no
 * observer needed. Mirrors the architecture premium e-commerce sites use on their PDPs
 * (sticky-sidebar wrapper at full content-area height + sticky element
 * inside).
 *
 * Mobile <990px: wrapper stays inside main-product (original DOM
 * position) so the buy box renders below the gallery in normal flow.
 * The pdp-mobile-cta-bar at the viewport bottom continues to handle the
 * persistent CTA on small screens.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = window.matchMedia('(min-width: 990px)');

  function getMain() {
    return document.querySelector('main#MainContent');
  }

  function getOriginalContainer() {
    // The .product--modern grid container inside <product-info> is where
    // info-wrapper originally lives (sibling of .product__media-wrapper).
    return document.querySelector('product-info .page-width .product--modern');
  }

  function applyLayout() {
    if (!document.body.classList.contains('template-product')) return;
    var main = getMain();
    if (!main) return;

    if (DESKTOP_MQ.matches) {
      moveSidebarToMain(main);
    } else {
      moveSidebarBackToOriginal(main);
    }
  }

  function moveSidebarToMain(main) {
    // Cleanup: remove orphaned wrappers left behind by a Theme-Editor
    // section reload (when main-product reloads, its child wrapper is
    // recreated, leaving our previously-moved one orphaned in <main>).
    var orphans = main.querySelectorAll(':scope > .product__info-wrapper');
    for (var i = 0; i < orphans.length; i++) orphans[i].remove();

    // Find current wrapper (now inside the freshly-loaded main-product).
    var orig = getOriginalContainer();
    if (!orig) return;
    var wrapper = orig.querySelector(':scope > .product__info-wrapper');
    if (!wrapper) return;

    main.appendChild(wrapper);
  }

  function moveSidebarBackToOriginal(main) {
    var moved = main.querySelector(':scope > .product__info-wrapper');
    if (!moved) return; // nothing to move back
    var orig = getOriginalContainer();
    if (!orig) return;
    orig.appendChild(moved);
  }

  function init() {
    applyLayout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // React to viewport crossing the 990px boundary (e.g. Theme Editor
  // device-preview switching, browser resize).
  if (typeof DESKTOP_MQ.addEventListener === 'function') {
    DESKTOP_MQ.addEventListener('change', applyLayout);
  } else if (typeof DESKTOP_MQ.addListener === 'function') {
    // Safari < 14 fallback
    DESKTOP_MQ.addListener(applyLayout);
  }

  // React to Theme Editor section reload — when Tom edits a setting on
  // main-product, Shopify swaps the section's HTML and our previously-
  // moved wrapper is now orphaned. Re-apply layout for the new wrapper.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.matches && e.target.matches('.shopify-section[id*="__main"]')) {
      applyLayout();
    }
  });

  // React to Theme Editor section unload — clean up orphaned wrappers
  // if the user removes main-product from the template.
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.matches && e.target.matches('.shopify-section[id*="__main"]')) {
      var main = getMain();
      if (!main) return;
      var orphans = main.querySelectorAll(':scope > .product__info-wrapper');
      for (var i = 0; i < orphans.length; i++) orphans[i].remove();
    }
  });
}());
